import { Lead } from '../types/lead';
import { metaValue } from './metaFields';

/**
 * Cal.com-Terminbuchung mit vorbefüllten Lead-Daten.
 *
 * Cal.com übernimmt Werte aus der Query-String direkt in die Buchungsmaske
 * (Prefill). Unterstützte Standard-Felder:
 *   name, email, notes, guests, attendeePhoneNumber, smsReminderNumber
 * Zusätzlich lassen sich eigene Booking-Fields per Identifier befüllen
 * und beliebige Werte als metadata[key] mitgeben.
 */

/** Standard-Buchungslink von Webzen Studio. */
const STANDARD_URL = 'https://cal.com/webzen-studio/kurz-besprechung-ph';

/**
 * Basis-URL in absteigender Priorität:
 *   1. calcom_url im Profil des Callers
 *   2. VITE_CALCOM_URL aus der .env
 *   3. STANDARD_URL
 */
const FALLBACK_URL =
  ((import.meta as any).env?.VITE_CALCOM_URL as string | undefined) || STANDARD_URL;

/**
 * Bringt die Telefonnummer in E.164-Form (+43.../+49...).
 *
 * Nationale Nummern (führende 0) werden anhand der Netzvorwahl zugeordnet:
 *   6[5-9]  → Österreich  (0664, 0699, 0676 …)
 *   1[5-7]  → Deutschland (0151, 0160, 0176 …) — ab 10 Stellen
 *   1 sonst → Österreich  (01 = Wien, 8–9 Stellen; in DE ist die 1 keine Ortsvorwahl)
 *
 * Die Längenprüfung trennt Wien (01 + 7–8 Ziffern) von deutschem Mobilfunk
 * (015x + 8 Ziffern), die sich sonst überschneiden würden.
 *
 * Festnetznummern außerhalb Wiens sind zwischen AT und DE nicht eindeutig
 * unterscheidbar (beide nutzen 02…–09…). Dann wird bewusst nichts geliefert,
 * damit Cal.com keine falsche Nummer erhält — die Original-Nummer steht
 * ohnehin vollständig im Notizen-Text.
 */
const normalizePhone = (phone?: string): string | undefined => {
  if (!phone) return undefined;
  const raw = phone.trim();
  if (!raw) return undefined;

  // Bereits international
  if (raw.startsWith('+')) return '+' + raw.slice(1).replace(/\D/g, '');

  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;

  // 00-Präfix ist die internationale Schreibweise von "+"
  if (digits.startsWith('00')) return '+' + digits.slice(2);

  // Ohne führende 0 keine nationale Nummer -> nicht zuordenbar
  if (!digits.startsWith('0')) return undefined;

  const national = digits.slice(1);
  if (/^6[5-9]/.test(national)) return '+43' + national;                     // AT Mobil
  if (/^1[5-7]/.test(national) && national.length >= 10) return '+49' + national; // DE Mobil
  if (/^1/.test(national) && national.length <= 9) return '+43' + national;   // Wien Festnetz

  return undefined; // mehrdeutiges Festnetz
};

/** Baut den Notizen-Text mit allen Infos, die im Termin sichtbar sein sollen. */
export const buildBookingNotes = (lead: Lead): string => {
  const strasse = metaValue(lead, 'strasse', 'street', 'adresse', 'address');
  const plz = metaValue(lead, 'plz', 'postleitzahl', 'zip', 'postal_code');
  const ort = metaValue(lead, 'ort', 'stadt', 'city');
  const adresse = [strasse, [plz, ort].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  const zeilen: Array<[string, string | undefined]> = [
    ['Anfrage', lead.name],
    ['Telefon', lead.phone],
    ['E-Mail', lead.email],
    ['Firma', lead.company || metaValue(lead, 'company')],
    ['Projekt', metaValue(lead, 'projekt')],
    ['Zeitraum', metaValue(lead, 'zeitraum')],
    ['Fläche', metaValue(lead, 'flaeche', 'qm', 'quadratmeter', 'm2', 'groesse', 'area', 'size')],
    ['Budget', metaValue(lead, 'budget')],
    ['Adresse', adresse || undefined],
    ['Kampagne', metaValue(lead, 'campaign')],
    ['Quelle', lead.source],
  ];

  return zeilen
    .filter(([, wert]) => wert && String(wert).trim())
    .map(([label, wert]) => `${label}: ${wert}`)
    .join('\n');
};

/**
 * Erzeugt die fertige Cal.com-URL für einen Lead.
 * Gibt null zurück, wenn keine Basis-URL konfiguriert ist.
 */
export const buildCalcomUrl = (lead: Lead, baseUrl?: string | null): string | null => {
  const base = (baseUrl || FALLBACK_URL || '').trim();
  if (!base) return null;

  let url: URL;
  try {
    url = new URL(base.startsWith('http') ? base : `https://${base}`);
  } catch {
    console.warn('Ungültige Cal.com-URL:', base);
    return null;
  }

  const setzen = (key: string, wert?: string | null) => {
    if (wert && String(wert).trim()) url.searchParams.set(key, String(wert).trim());
  };

  setzen('name', lead.name);
  setzen('email', lead.email);
  setzen('notes', buildBookingNotes(lead));

  const telefon = normalizePhone(lead.phone);
  // Beide Varianten: je nach Event-Konfiguration heißt das Feld anders
  setzen('attendeePhoneNumber', telefon);
  setzen('smsReminderNumber', telefon);

  // Damit sich die Buchung später dem Lead zuordnen lässt
  setzen('metadata[leadId]', lead.id);

  return url.toString();
};
